/**
 * PO Agent Response Processor
 *
 * Specialized processor for Purchase Order Agent.
 * Handles control messages and dynamic component triggering.
 *
 * @module services/responseProcessors/POAgentProcessor
 */

import {
  IResponseProcessor,
  NormalizedResponse,
  ProcessingResult,
  ProcessorContext,
  PO_ACTION_TYPES,
  isPOActionType,
} from './types';
import { ChatMessageResponse } from '@/collections/chat';
import { executeAgentAction } from '@/config/agentActions';

/**
 * Processor for Purchase Order Agent
 *
 * Handles:
 * - Control message detection (DISPLAY_SUPPLIERS_LIST, etc.)
 * - JSON parsing of action messages
 * - Separation of regular vs action messages
 * - PO preview action execution
 */
export class POAgentProcessor implements IResponseProcessor {
  readonly agentIds = ['purchase-order', 'po-agent'];
  readonly description = 'Processor for PO Agent with control message handling';

  /**
   * Normalize response with PO Agent specific message filtering
   */
  normalizeResponse(response: ChatMessageResponse): NormalizedResponse {
    const responseAny = response as any;
    let messageContent = '';
    let events: any[] = [];
    let metadata = response.metadata || {};
    let isNewFormat = false;
    const regularMessages: Array<{ content: string; agent?: string }> = [];
    const actionMessages: Array<{ action: string; parameters: any; agent?: string }> = [];

    // Check for messages array (PO Agent can have at top level or in reply)
    const messagesArray =
      responseAny.reply?.messages || responseAny.messages || [];

    if (Array.isArray(messagesArray) && messagesArray.length > 0) {
      isNewFormat = true;

      // Separate control messages from regular messages
      for (const msg of messagesArray) {
        if (!msg.content || typeof msg.content !== 'string') continue;

        let isActionMessage = false;

        // Try to parse as JSON action message
        try {
          const parsed = JSON.parse(msg.content);
          if (parsed.action && isPOActionType(parsed.action)) {
            isActionMessage = true;
            actionMessages.push({
              action: parsed.action,
              parameters: parsed.parameters || {},
              agent: msg.agent,
            });
          }
        } catch (e) {
          // Not JSON - check for legacy string-based control messages
          const matchedAction = PO_ACTION_TYPES.find((actionType) =>
            msg.content.includes(actionType)
          );
          if (matchedAction) {
            isActionMessage = true;
            actionMessages.push({
              action: matchedAction,
              parameters: {},
              agent: msg.agent,
            });
          }
        }

        if (!isActionMessage) {
          regularMessages.push({
            content: msg.content,
            agent: msg.agent,
          });
        }
      }

      // Build message content from regular messages only
      messageContent = regularMessages
        .map((msg) => msg.content)
        .filter(Boolean)
        .join('\n\n');
    }

    // Extract events if present and is an array
    events = Array.isArray(responseAny.reply?.events) ? responseAny.reply.events : [];

    // Clean up any remaining control message references in content
    for (const actionType of PO_ACTION_TYPES) {
      messageContent = messageContent.replaceAll(actionType, '').trim();
    }

    // Filter out handoff signals
    messageContent = messageContent.replace(/\[HANDOFF_?SIGNAL\]/g, '').trim();

    return {
      messageContent,
      events,
      metadata,
      isNewFormat,
      regularMessages,
      actionMessages,
    };
  }

  /**
   * Process response with PO Agent specific logic
   */
  async processResponse(
    normalized: NormalizedResponse,
    context: ProcessorContext
  ): Promise<ProcessingResult> {
    const result: ProcessingResult = {
      messagesToAdd: [],
      messagesToRemove: [],
      eventBusEmits: [],
      success: true,
    };

    // Step 1: Add conversation messages
    this.addConversationMessages(normalized, context, result);

    // Step 2: Trigger PO preview action (from events OR messages, not both)
    this.triggerPOPreviewAction(normalized, context);

    return result;
  }

  /**
   * Add regular messages to conversation result
   */
  private addConversationMessages(
    normalized: NormalizedResponse,
    context: ProcessorContext,
    result: ProcessingResult
  ): void {
    if (normalized.regularMessages.length > 0) {
      for (const msg of normalized.regularMessages) {
        if (msg.content && msg.content.trim()) {
          result.messagesToAdd.push({
            id: context.generateMessageId(),
            content: msg.content,
            timestamp: new Date(),
            isUser: false,
            agent: {
              name: msg.agent || context.selectedAgent.name,
              avatar: context.selectedAgent.avatar,
            },
          });
        }
      }
    } else if (normalized.messageContent.trim()) {
      // Fallback: add normalized content if no regular messages extracted
      result.messagesToAdd.push({
        id: context.generateMessageId(),
        content: normalized.messageContent,
        timestamp: new Date(),
        isUser: false,
        agent: {
          name: context.selectedAgent.name,
          avatar: context.selectedAgent.avatar,
        },
      });
    }
  }

  /**
   * Trigger PO preview action from a single source
   *
   * Actions can come from two sources:
   * 1. Events array (new format) - preferred
   * 2. Action messages (legacy format) - fallback
   *
   * Only ONE source is used to prevent duplicate preview triggers.
   */
  private triggerPOPreviewAction(
    normalized: NormalizedResponse,
    context: ProcessorContext
  ): void {
    const actionContext = {
      sessionId: context.conversationId,
      agentId: context.selectedAgent.id,
      userId: context.userId,
    };

    // Check for action in events array (new format - preferred)
    const poActionEvent = normalized.events.find(
      (e) =>
        e.type === 'action' &&
        e.metadata?.action &&
        isPOActionType(e.metadata.action as string)
    );

    if (poActionEvent) {
      // Use events-based action (preferred path)
      const chatResponsePayload = {
        chat_response: {
          actions: [
            {
              action: poActionEvent.metadata!.action,
              parameters: poActionEvent.metadata!.parameters || {},
              agent: (poActionEvent as any).agent,
            },
          ],
        },
      };
      setTimeout(() => {
        executeAgentAction(
          'show_po_agent_preview',
          chatResponsePayload,
          actionContext
        );
      }, 100);
      return; // Exit early - action triggered from events
    }

    // Fallback: use action messages (legacy format)
    if (normalized.actionMessages.length > 0) {
      setTimeout(() => {
        executeAgentAction(
          'show_po_agent_preview',
          {
            chat_response: {
              messages: normalized.regularMessages,
              actions: normalized.actionMessages,
            },
            actions: normalized.actionMessages,
          },
          actionContext
        );
      }, 100);
    }
  }
}
